import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, FeaturedIcon, Modal, Spinner, Tooltip } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { ArrowLeft, FileSearch02, MessageDotsSquare, MoonStar } from '@untitled-ui/icons-react';
import classNames from 'classnames';

import { BookWithClinicForm, ClinicBookingInformationForm } from "src/components/forms/BookWithClinicForm";
import { AutomedClinicAvailabilityTimeModel, AutomedDoctorAvailabilityTimeModel, availabilityService, ServiceTypeSearchEnum } from 'src/services/availability';
import { useDebounce } from 'src/hooks/useDebounce';
import { Availability } from 'src/components/availability';
import { useMessageListener } from 'src/contexts/message';
import { filterToAfterHoursAppointmentTypes, filterToStandardAppointmentTypes, isFaceToFace } from 'src/utils/appointmentTypes';
import { bookingService, BookingUserProfileModel } from 'src/services/booking';
import moment from 'moment';
import { SiteBaseModel } from 'src/services/site';
import { umbracoService } from 'src/services/umbraco';
import DoctorIcon from 'src/assets/doctor.svg?react';
import { SentenceList } from 'src/utils/sentenceList';
import { AfterHoursAvailability } from 'src/components/availability/AfterHoursAvailability';
import { useDispatch } from 'src/store';
import { setClinicId } from 'src/store/appointment';
import { clinicName, doctorName } from 'src/utils/name';
import { captureException } from '@sentry/react';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { toAutomedDoctorAvailabilityTimeModel } from 'src/utils/mapAutomedTimeTypes';
import { innerHtml, openWindowViaLink } from 'src/utils/html';

type Props = {
    toggleAvailablilty?: boolean;
    afterHoursControls?: boolean;
}

function selectedDoctorIsDisplayOnly(doctorId: string | number, doctors: BookingUserProfileModel[]) {
    return doctors.find(d => d.userId === doctorId)?.displayProfileNoOnlineBooking;
}

function TabButton({ selected, disabled, onClick, children, Icon }: {
    selected?: boolean,
    disabled?: boolean,
    onClick?(): unknown,
    children?: React.ReactNode,
    Icon?: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode
}) {
    return <button className={classNames('px-4 sm:px-6 py-2.5 border-gray-200 rounded-t-md border font-semibold text-sm translate-y-px disabled:bg-gray-100 disabled:text-gray-400 leading-tight', {
        "text-primary border-b-transparent bg-white": selected,
        "bg-gray-50": !selected
    })}
        onClick={onClick}
        disabled={disabled}
    >
        {Icon && <Icon className='w-4 h-4 mr-2 hidden sm:inline-block' />}
        {children}
    </button>
}

export function BookWithClinic({ toggleAvailablilty, afterHoursControls }: Props) {
    const site = useLoaderData() as SiteBaseModel;
    const { data: umbracoSite } = useClinicSelector();

    const clinicId = site.id;
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setClinicId(clinicId))
    }, [clinicId, dispatch])

    const [urlSearchParams] = useSearchParams();
    const afterHoursEnabled = urlSearchParams.has('afterHours') && urlSearchParams.get("afterHours") === "True";

    const manageBookingUrl = `${window.location.origin}/manage/site/${site.automedSiteId}`;

    const [serviceType, setServiceType] = useState<ServiceTypeSearchEnum>(ServiceTypeSearchEnum.Generic);
    const [showAvailability, setShowAvailability] = useState(false);
    const [page, setPage] = useState<1 | 2>(1);
    const [doctorIds, setDoctorIds] = useState<{ doctorId: number, ts: AutomedClinicAvailabilityTimeModel }[]>([]);
    const [appointmentDate, setAppointmentDate] = useState<Date>();
    const [bookingQuery, setValues] = useState<ClinicBookingInformationForm>();
    const [isAvailablityDisabled, setIsAvailablityDisabled] = useState(false);
    const [isAlertShown, setIsAlertShown] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const openAlert = useCallback((message: string) => {
        setIsAlertShown(true);
        setAlertMessage(message);
    }, []);

    const { isLoading: isAppointmentTypesLoading, data: appointmentTypes } = useAsyncData({
        service: availabilityService,
        method: availabilityService.GetAppointmentTypes,
    }, Number(clinicId));

    const { isLoading: isDoctorsLoading, data: doctors } = useAsyncData({
        service: bookingService,
        method: bookingService.GetDoctorsAtSite
    }, Number(clinicId));

    const { invoke: getBookingToken, isLoading: isLoadingToken, ...tokenRequest } = useAsyncData({
        service: umbracoService,
        method: umbracoService.GetBookingToken,
        invokeOnMount: false
    });

    const afterHoursAppointmentTypes = useMemo(() => filterToAfterHoursAppointmentTypes(appointmentTypes), [appointmentTypes]);
    const standardAppointmentTypes = useMemo(() => filterToStandardAppointmentTypes(appointmentTypes), [appointmentTypes]);

    const initialValues = useMemo(() => {
        if (doctors && appointmentTypes) {
            const defaultAppointment = appointmentTypes.find(apt => apt.isDefaultAppt) ?? appointmentTypes[0];
            return {
                patientType: "existing",
                appointmentMode: isFaceToFace(defaultAppointment) ? "faceToFace" : "telehealth",
                appointmentTypeId: defaultAppointment.appointmentTypeId,
                doctorId: "any"
            } as Partial<ClinicBookingInformationForm>;
        }
    }, [appointmentTypes, doctors]);

    const changeValues = useDebounce(
        useCallback((newValues: ClinicBookingInformationForm) => {
            setValues({ ...newValues });
        }, []),
        300);

    const onValidate = useCallback((valid: boolean) => {
        setIsAvailablityDisabled(!valid);
    }, []);

    const openApp = useCallback(async (doctor: string, date: Date, appointmentTypeId: string, patientType: "new" | "existing", timeSlot: AutomedClinicAvailabilityTimeModel | AutomedDoctorAvailabilityTimeModel) => {
        const newWindow = window.open();
        try {
            const { access_token } = await getBookingToken();
            const query = new URLSearchParams({
                dateTime: moment(date).toISOString(),
                doctor: doctor,
                clinic: clinicId.toString(),
                appointment: appointmentTypeId,
                new: (patientType === "new").toString(),
                _booking: access_token
            });
            const mappedTimeSlot = toAutomedDoctorAvailabilityTimeModel(timeSlot);
            for (const [key, value] of Object.entries(mappedTimeSlot)) {
                if (key && value) {
                    query.append(`timeSlot.${key}`, value);
                }
            }
            const url = `${window.location.origin}/app?${query}`;
            if (newWindow) {
                newWindow.location = url;
            }

            if (!newWindow || newWindow.closed) {
                // Fallback to open via link
                openWindowViaLink(url);
                captureException(`Attempting to open booking app with fallback.`, {
                    level: 'warning',
                    extra: {
                        query: Object.fromEntries(query)
                    }
                });
            }
        } catch (e) {
            newWindow?.close();
            captureException(e);
        }
    }, [clinicId, getBookingToken]);

    useMessageListener(useCallback((payload) => {
        if (typeof payload === "object" && payload.message === 'myhealth-booking-ui-after-hours') {
            setServiceType(payload.value ? ServiceTypeSearchEnum.AfterHours : ServiceTypeSearchEnum.Generic)
        }
    }, []));

    const appointmentType = useMemo(() => appointmentTypes?.find(apt => apt.appointmentTypeId === bookingQuery?.appointmentTypeId), [appointmentTypes, bookingQuery?.appointmentTypeId]);

    if (isAppointmentTypesLoading || isDoctorsLoading) {
        return <Spinner size='lg' className='fixed inset-0 flex items-center justify-center' />
    }

    return <>
        {afterHoursControls && <div className='flex flex-row gap-2 sm:gap-3 pr-3'>
            <TabButton
                selected={serviceType === ServiceTypeSearchEnum.Generic}
                onClick={() => setServiceType(ServiceTypeSearchEnum.Generic)}
                Icon={DoctorIcon}
            >
                General care
            </TabButton>
            {!afterHoursEnabled
                ? <Tooltip
                    attachmentPoint='bottom'
                    tooltipContent="After-hours booking not available while clinic is open."
                >
                    <TabButton
                        disabled
                        Icon={MoonStar}
                    >
                        After hours
                    </TabButton>
                </Tooltip>
                : <TabButton
                    selected={serviceType === ServiceTypeSearchEnum.AfterHours}
                    onClick={() => setServiceType(ServiceTypeSearchEnum.AfterHours)}
                    Icon={MoonStar}
                >
                    After hours
                </TabButton>}
            <TabButton
                selected={false}
                onClick={() => window.open(manageBookingUrl)}
                Icon={FileSearch02}
            >
                Manage Booking
            </TabButton>
        </div>}
        <div
            className={classNames('bg-white rounded-xl border border-gray-200', {
                'rounded-tl-none': afterHoursControls
            })}>
            {serviceType === ServiceTypeSearchEnum.Generic && <>
                <Transition show={!bookingQuery || page === 1} as="div" className="transition duration-300 data-[enter]:data-[closed]:-translate-x-full data-[leave]:data-[closed]:-translate-x-full data-[leave]:data-[closed]:absolute w-full inset-0">
                    <div className='space-y-4 pt-6 pb-5'>

                        <h3 className='px-4'>
                            <span className='block font-semibold text-lg text-gray-900'>Book with {clinicName(site)}</span>
                            <small className='block mt-1 font-light text-sm text-gray-600'>Your selections will help show the right availability.</small>
                        </h3>

                        <hr />

                        <BookWithClinicForm
                            className='px-4'
                            doctors={doctors}
                            appointmentTypes={standardAppointmentTypes}
                            onNewValues={changeValues}
                            onValidate={onValidate}
                            defaultValues={bookingQuery || initialValues}
                        />
                        {(toggleAvailablilty && !showAvailability)
                            ? <div className="px-4">
                                <Button onClick={() => {
                                    setShowAvailability(true)
                                }} className='w-full'>View availability</Button>
                            </div>
                            : bookingQuery && appointmentType && (bookingQuery.doctorId === "any"
                                ? <Availability
                                    disabled={isLoadingToken || isAvailablityDisabled}
                                    appointmentType={appointmentType}
                                    clinicId={Number(clinicId)}
                                    onSelectTimeSlot={({ date, doctorId, timeSlots }) => {
                                        if (doctorId.length > 1) {
                                            setPage(2);
                                            setAppointmentDate(date);
                                            setDoctorIds(doctorId.map((d, i) => ({ doctorId: d, ts: timeSlots[i % timeSlots.length] })));
                                        } else {
                                            openApp(doctorId[0].toString(), date, bookingQuery.appointmentTypeId, bookingQuery.patientType, timeSlots[0])
                                        }
                                    }}
                                    futureApptWeeks={umbracoSite?.futureApptWeeks}
                                />
                                : <Availability
                                    errorElement={selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors)
                                        ? <>
                                            <p><strong>This provider does not accept online bookings.</strong></p>
                                            <p>Please select another provider or contact the centre to make an appointment on{' '}
                                                <a className="text-primary" href={`tel:${umbracoSite?.phoneNumber}`} >{umbracoSite?.phoneNumber}</a>.</p>
                                        </>
                                        : undefined
                                    }
                                    disabled={isLoadingToken || isAvailablityDisabled || selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors)}
                                    doctorId={bookingQuery.doctorId}
                                    appointmentType={appointmentType}
                                    clinicId={Number(clinicId)}
                                    onSelectTimeSlot={({ date, doctorId, timeSlot }) => {
                                        if (appointmentType.displayOnly === "1") {
                                            openAlert(appointmentType.displayOnlyMessage);
                                        } else {
                                            openApp(doctorId.toString(), date, bookingQuery.appointmentTypeId, bookingQuery.patientType, timeSlot)
                                        }
                                    }}
                                    futureApptWeeks={umbracoSite?.futureApptWeeks}
                                />)
                        }
                        {tokenRequest?.error && <div className='text-error font-medium text-base px-4'>{tokenRequest.error.message || tokenRequest.error.name}</div>}
                    </div>
                    <Modal
                        backdropClassname='opacity-0'
                        open={isAlertShown}
                        onClose={() => setIsAlertShown(false)}
                        size='sm'
                        title={<FeaturedIcon />}
                    >
                        <div className='prose-sm' {...innerHtml(alertMessage)} />
                    </Modal>
                </Transition>
                <Transition show={!!bookingQuery && page === 2} as="div" className="transition duration-300 data-[enter]:data-[closed]:translate-x-full data-[leave]:data-[closed]:translate-x-full data-[leave]:data-[closed]:absolute w-full inset-0">
                    <div className='space-y-4 py-5 px-4'>
                        <button className='text-gray-600 font-semibold text-base flex flex-row gap-2' onClick={() => {
                            setPage(1);
                            setDoctorIds([]);
                            setAppointmentDate(undefined)
                        }
                        }>
                            <ArrowLeft />
                            Back</button>
                        <div>
                            <h2 className='font-semibold text-gray-900 text-lg mb-1'>Select a doctor at {clinicName(site)}</h2>
                            <p>
                                {bookingQuery?.patientType === "existing" ? "Existing patient" : "New patient"}
                                {' • '}
                                {bookingQuery?.appointmentMode === "faceToFace" ? "Face to face appointment" : "Telehealth appointment"}
                                <br />
                                {moment(appointmentDate).format("ddd D MMM, LT")}
                            </p>
                        </div>

                    </div>
                    <hr />
                    <div className="p-4 space-y-4">
                        {doctorIds.map(({ doctorId, ts }) => {
                            const doc = doctors.find(d => d.userId === doctorId.toString());
                            if (!doc) {
                                return null;
                            }
                            return <div key={doctorId} className='p-4 rounded-lg border border-grey-300 flex flex-col sm:flex-row gap-5'>
                                <div className='flex flex-col gap-2.5 flex-1'>
                                    <div className='flex flex-row gap-2.5'>
                                        <img
                                            className='size-16 sm:size-24 object-fit-cover rounded-lg border border-grey-200'
                                            src={doc.umbracoProfileUrl ? `${import.meta.env.VITE_MYHEALTH_WEBSITE}${doc.umbracoProfileUrl}` : doc.automedProfileUrl}
                                        />
                                        <div className=''>
                                            <h3 className='font-semibold text-gray-700 text-lg'>
                                                {doctorName(doc)}
                                            </h3>
                                            <p className='text-gray-700 text-sm font-medium'>
                                                {[doc.umbracoRole, doc.gender === "M" ? "Male" : "Female"].filter(x => x).join(', ')}
                                            </p>
                                            <p className='text-xs text-gray-500 font-medium'>
                                                {doc.umbracoQualification}
                                            </p>
                                            {doc.umbracoLanguages?.length && <ul className='hidden sm:block mt-1 text-gray-500 text-sm font-normal'>
                                                <li className='flex flex-row'>
                                                    <MessageDotsSquare className='w-5 h-5 mr-2.5' />
                                                    Speaks {SentenceList(doc.umbracoLanguages)}
                                                </li>
                                            </ul>}
                                        </div>
                                    </div>
                                    {doc.umbracoLanguages?.length && <ul className='block sm:hidden mt-1 text-gray-500 text-sm font-normal'>
                                        <li className='flex flex-row'>
                                            <MessageDotsSquare className='w-5 h-5 mr-2.5' />
                                            Speaks {SentenceList(doc.umbracoLanguages)}
                                        </li>
                                    </ul>}
                                </div>
                                <div className='flex-none flex flex-col content-end space-y-5 text-center'>
                                    {doc.umbracoPageUrl && <Button target='_blank' size='lg' color='gray' href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${doc.umbracoPageUrl}`}>
                                        View Profile
                                    </Button>}
                                    <Button
                                        disabled={isLoadingToken}
                                        onClick={() => openApp(doc.userId.toString(), appointmentDate!, bookingQuery!.appointmentTypeId, bookingQuery!.patientType, ts)}
                                        size='lg' className='min-w-32'>Book</Button>
                                </div>
                            </div>
                        })}
                    </div>
                </Transition>
            </>}
            {serviceType === ServiceTypeSearchEnum.AfterHours && umbracoSite &&
                (umbracoSite.isAfterHoursServiceAvailable
                    ? <>
                        <div className='p-6 text-gray-700 text-sm'>
                            <h1 className='text-gray-900 font-semibold text-lg mb-1'>{umbracoSite.afterHoursServiceTitle || "Book After-hours Care service"}</h1>
                            {umbracoSite.afterHoursServiceContentHtml
                                ? <div className='prose-sm' {...innerHtml(umbracoSite.afterHoursServiceContentHtml)} />
                                : <>
                                    <p className='mb-[1em]' >
                                        <strong className='font-medium'>Phone service for existing patients for 'Urgent' matters that can't wait untill regular clinic hours. It is a privately charged service with no Medicare rebate.</strong>
                                    </p>
                                    <p>For medical emergencies that require immediate medical attention, please consider your local Hospital Emergency Department or calling '000".</p>
                                </>}
                        </div>
                        <hr />
                        <h2 className='py-2.5 px-4 bg-gray-50 text-gray-900 font-medium text-lg'>
                            Select a timeslot
                        </h2>
                        <AfterHoursAvailability
                            siteId={clinicId}
                            className='p-4'
                            appointmentTypes={afterHoursAppointmentTypes}
                            onSelectTimeSlot={({ date, doctorId, appointmentTypeId, timeSlot }) => {
                                if (doctorId.length === 1) {
                                    openApp(doctorId[0].toString(), date, appointmentTypeId, "new", timeSlot)
                                }
                            }}
                        />
                    </>
                    : <div className='p-6 text-gray-700 text-sm'>
                        <h1 className='text-gray-900 font-semibold text-lg mb-1'>{umbracoSite.afterHoursServiceTitle || "Book After-hours Care service"}</h1>
                        <div className='prose-sm' {...innerHtml(umbracoSite.afterHoursServiceDisabledContentHtml)} />
                    </div>
                )}
        </div>
    </>
}